/**
 * CHOREOGRAPHY TIMING the card side needs (B8). Pure; mirrors design-spec §E.
 * D is the route length in km.
 *
 *   Tc    = clamp(1100 + 320·log2(1 + D/4), 1100, 2400)   camera
 *   Td    = clamp(1100 + 460·log2(1 + D/4), 1100, 3400)   line draw
 *   Tarr  = 0.4·Tc + Td                                    arrival; the card enters at Tarr − 120
 *   Tdrive= clamp(8000 + 6000·log2(1 + D/4), 10000, 40000)
 *
 * B4 owns the real sequence. The card uses these only as fallbacks (when a
 * route event is late) and to estimate how long a road span lasts in a drive.
 */
const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
const lg = (D: number): number => Math.log2(1 + Math.max(0, D) / 4);

export const tcMs = (D: number): number => clamp(1100 + 320 * lg(D), 1100, 2400);
export const tdMs = (D: number): number => clamp(1100 + 460 * lg(D), 1100, 3400);
export const drawStartMs = (D: number): number => 0.4 * tcMs(D);
export const tarrMs = (D: number): number => drawStartMs(D) + tdMs(D);
export const tdriveMs = (D: number): number => clamp(8000 + 6000 * lg(D), 10000, 40000);

/** The card (and the meter's dock) lead the arrival by this much (§E.1). */
export const CARD_LEAD_MS = 120;

/** ms after `route:start` at which the card enters and the meter docks. */
export const dockDelayMs = (durationMs: number): number => Math.max(0, durationMs - CARD_LEAD_MS);

/** Drive ramps: p follows an eased start and end over this long (§E.6). */
export const DRIVE_RAMP_MS = 1500;

/**
 * Drive progress p at time t (0..T): speed ramps up over the first `ramp` ms,
 * holds, and ramps down over the last `ramp` ms (smoothstep speed profile), so p
 * is linear in the middle and eased at both ends. Monotonic, p(0)=0, p(T)=1.
 */
export function driveP(t: number, T: number, ramp = DRIVE_RAMP_MS): number {
  if (!(T > 0)) return 1;
  const u = clamp(t / T, 0, 1);
  const w = clamp(ramp / T, 0, 0.5);
  if (w === 0) return u;
  const S = (x: number): number => x * x * x - (x * x * x * x) / 2; // ∫ smoothstep, S(1) = 0.5
  const area = 1 - w; // 2 · (w/2) + (1 − 2w)
  let F: number;
  if (u < w) F = w * S(u / w);
  else if (u <= 1 - w) F = w / 2 + (u - w);
  else F = area - w * S((1 - u) / w);
  return clamp(F / area, 0, 1);
}

/** Inverse of driveP: the time at which the drive reaches progress p (bisection). */
export function driveTimeAt(p: number, T: number, ramp = DRIVE_RAMP_MS): number {
  if (p <= 0) return 0;
  if (p >= 1) return T;
  let lo = 0;
  let hi = T;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (driveP(mid, T, ramp) < p) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** How long (ms) the drive spends on a stretch [fromM, toM] of a route of lengthM. */
export function spanTravelMs(fromM: number, toM: number, lengthM: number, T: number): number {
  if (!(lengthM > 0)) return 0;
  return driveTimeAt(toM / lengthM, T) - driveTimeAt(fromM / lengthM, T);
}
